// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Pointer-driven chart scrubbing + zoom + explorer cursor controls:
// - setupChartScrubber: pointer drag/click on the flight + explorer
//   canvases (drag-to-zoom, click-to-seek, reset-zoom buttons), plus the
//   touch gesture layer (one-finger inspect, pinch zoom, two-finger pan,
//   double-tap zoom) and the zoom-step / select-region toolbar buttons.
// - finishZoomDrag: commit a drag-zoom selection if wide enough.
// - setExplorerCursorRatio / setExplorerCursorElapsed: external entry
//   points to move the explorer cursor (used by marker jumps and
//   timeline pointer drags).
// - scheduleExplorerInteractionUpdate: rAF-coalesced explorer overlay
//   refresh.
// - explorerTimelineControlIcon: tiny markup helper for prev/next
//   buttons (shared with the chart-renderers timeline label).
// - explorerFindingMarkers / explorerFindingMarkerPosition /
//   jumpExplorerFindingMarker: timeline finding-marker navigation.
// - setupExplorerContextInteractions: click + pointerdown on the
//   explorer panel (alert chips, alert jump, timeline step buttons,
//   alert button toggles, timeline marker click, timeline body drag).

import {
  chartGestureHintText,
  pinchZoomWindow,
  steppedZoomWindow,
} from "../charts/presentation.js";

// Touch gesture tuning: taps must stay short and still; a second tap close in
// time and space to the first zooms in around the tapped moment.
const TOUCH_TAP_MAX_MS = 350;
const TOUCH_TAP_SLOP_PX = 12;
const TOUCH_DOUBLE_TAP_MAX_GAP_MS = 400;
const TOUCH_DOUBLE_TAP_SLOP_PX = 40;
const TOUCH_DOUBLE_TAP_ZOOM_FACTOR = 0.5;

export function createChartScrubber({
  clamp01,
  flightChartPadding,
  explorerChartPadding,
  flightChartLayoutOptions,
  explorerChartLayoutOptions,
  chartPointerGeometry,
  scheduleMarkerUpdate,
  scheduleDrawAll,
  updateExplorerChartInteraction,
  explorerPhaseFilteredData,
  nearestPoint,
  updateScrubReadout,
  drawExplorerChart,
  explorerTimelineRatioForElapsed,
  buildExplorerTimelineMarkers,
  visibleScrubRatio,
  renderExplorerContext,
  getZoomRange,
  setZoomRange,
  getScrubRatio,
  setScrubRatio,
  getDragSelection,
  setDragSelection,
  getExplorerPilotObservationPanelOpen = () => false,
  getExplorerPilotObservationSelectionActive = () => false,
  commitExplorerPilotObservationSelection = () => {},
  getExplorerGamiSweepPanelOpen = () => false,
  getExplorerGamiSweepSelectionActive = () => false,
  commitExplorerGamiSweepSelection = () => {},
  showExplorerGamiCursorTime = () => {},
  hideExplorerGamiCursorTime = () => {},
  updateChartCursorPosition = () => {},
  getExplorerSelectedAlertId,
  setExplorerSelectedAlertId,
  getExplorerInteractionUpdateScheduled,
  setExplorerInteractionUpdateScheduled,
}) {
  // Spacebar-held drag-to-pan + arrow-key nudge, active only while the chart
  // is zoomed in. State lives at the closure scope so the keyboard handlers,
  // hover tracking, and cursor styling stay in sync across both canvases.
  let chartCanvases = [];
  let hoveredCanvas = null;
  let spaceHeld = false;

  // Touch gesture state. One finger inspects (crosshair scrub), two fingers
  // pinch-zoom/pan the time axis, and a finger left down after a pinch ends
  // is ignored until lifted so the crosshair doesn't jump.
  const touchPoints = new Map();
  const ignoredTouchPointerIds = new Set();
  let pinchState = null;
  let touchTapCandidate = null;
  let lastTouchTap = null;
  // Select Region mode: the next one-finger (or mouse) drag selects a time
  // range and zooms to it, then the mode exits.
  let selectRegionActive = false;

  const coarsePointerInput = () => typeof window !== "undefined"
    && typeof window.matchMedia === "function"
    && window.matchMedia("(hover: none) and (pointer: coarse)").matches;

  const chartGestureHintBaseText = () => chartGestureHintText({
    coarse: coarsePointerInput(),
    selectRegion: selectRegionActive,
  });

  const refreshChartGestureHints = () => {
    if (typeof document === "undefined") return;
    document.querySelectorAll(".chart-gesture-hint").forEach((hint) => {
      // A marked-window controller owns the explorer hint while selection is
      // active; leave its copy in place.
      if (
        (getExplorerPilotObservationSelectionActive() || getExplorerGamiSweepSelectionActive())
        && hint.closest(".explorer-chart-wrap")
      ) return;
      hint.textContent = chartGestureHintBaseText();
    });
  };

  const setChartSelectRegionActive = (value) => {
    const next = value === true;
    if (selectRegionActive === next) return;
    selectRegionActive = next;
    if (typeof document === "undefined") return;
    document.querySelectorAll("[data-chart-select-region]").forEach((button) => {
      button.classList.toggle("is-active", next);
      button.setAttribute("aria-pressed", next ? "true" : "false");
    });
    chartCanvases.forEach((canvas) => canvas.classList.toggle("is-selecting-region", next));
    refreshChartGestureHints();
  };

  const isChartZoomed = () => {
    const zoomRange = getZoomRange();
    return !(zoomRange.start <= 0 && zoomRange.end >= 1);
  };

  // Shift the visible window by deltaStart (absolute 0-1 ratio), keeping its
  // width fixed and clamping so it can't run past either end of the flight.
  const panZoomBy = (deltaStart) => {
    const zoomRange = getZoomRange();
    const width = zoomRange.end - zoomRange.start;
    let start = zoomRange.start + deltaStart;
    start = Math.max(0, Math.min(start, 1 - width));
    setZoomRange({ start, end: start + width });
  };

  const updateCanvasCursors = () => {
    const dragSelection = getDragSelection();
    const grabReady = spaceHeld && isChartZoomed();
    chartCanvases.forEach((canvas) => {
      if (dragSelection?.mode === "pan" && dragSelection.canvasId === canvas.id) {
        canvas.style.cursor = "grabbing";
      } else if (grabReady) {
        canvas.style.cursor = "grab";
      } else {
        canvas.style.cursor = "";
      }
    });
  };

  const cancelActivePanDrag = () => {
    if (getDragSelection()?.mode !== "pan") return;
    setDragSelection(null);
    scheduleDrawAll();
  };

  function setupChartScrubber() {
    const canvases = [
      document.querySelector("#flightChart"),
      document.querySelector("#explorerChart"),
    ].filter(Boolean);
    chartCanvases = canvases;

    const pointerGeometryForCanvas = (canvas) => {
      const isExplorer = canvas.id.startsWith("explorer");
      return chartPointerGeometry(
        canvas,
        isExplorer ? explorerChartPadding : flightChartPadding,
        isExplorer ? explorerChartLayoutOptions : flightChartLayoutOptions,
      );
    };

    const pointerInsideChartPlot = (canvas, event) => {
      const geometry = pointerGeometryForCanvas(canvas);
      return (
        event.clientX >= geometry.left
        && event.clientX <= geometry.right
        && event.clientY >= geometry.top
        && event.clientY <= geometry.bottom
      );
    };

    const ratioFromClientX = (canvas, clientX) => {
      const geometry = pointerGeometryForCanvas(canvas);
      return clamp01((clientX - geometry.left) / geometry.width);
    };

    const absoluteRatio = (visibleRatio) => {
      const zoomRange = getZoomRange();
      return zoomRange.start + visibleRatio * (zoomRange.end - zoomRange.start);
    };

    const updateFromClientX = (canvas, clientX) => {
      setScrubRatio(absoluteRatio(ratioFromClientX(canvas, clientX)));
      scheduleMarkerUpdate(canvas.id);
    };

    const updateCursorPosition = (canvas, event) => {
      updateChartCursorPosition({ canvasId: canvas.id, clientX: event.clientX, clientY: event.clientY });
    };

    const dragModeForCanvas = (canvas) => {
      if (canvas.id === "explorerChart" && getExplorerPilotObservationSelectionActive()) return "pilot-observation";
      if (canvas.id === "explorerChart" && getExplorerGamiSweepSelectionActive()) return "gami-sweep";
      if (spaceHeld && isChartZoomed()) return "pan";
      return "zoom";
    };

    const showMarkedWindowCursorTime = (canvas, event, ratio) => {
      if (
        canvas.id !== "explorerChart"
        || (!getExplorerPilotObservationSelectionActive() && !getExplorerGamiSweepSelectionActive())
      ) return;
      showExplorerGamiCursorTime({ clientX: event.clientX, clientY: event.clientY, ratio });
    };

    const touchPointsForCanvas = (canvasId) => [...touchPoints.values()]
      .filter((point) => point.canvasId === canvasId);

    const touchDistance = (a, b) => Math.max(1, Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY));

    const zoomAroundClientX = (canvas, clientX, factor) => {
      const center = absoluteRatio(ratioFromClientX(canvas, clientX));
      setZoomRange(steppedZoomWindow(getZoomRange(), factor, center));
      setScrubRatio(center);
      scheduleDrawAll();
    };

    const beginPinch = (canvas, canvasTouches) => {
      touchTapCandidate = null;
      lastTouchTap = null;
      if (getDragSelection()?.canvasId === canvas.id) {
        setDragSelection(null);
        if (canvas.id === "explorerChart") hideExplorerGamiCursorTime();
        scheduleMarkerUpdate(canvas.id);
      }
      const [a, b] = canvasTouches;
      const zoomRange = getZoomRange();
      pinchState = {
        canvasId: canvas.id,
        pointerIds: [a.pointerId, b.pointerId],
        startDistance: touchDistance(a, b),
        anchorZoom: { start: zoomRange.start, end: zoomRange.end },
        anchorMidRatio: ratioFromClientX(canvas, (a.clientX + b.clientX) / 2),
      };
    };

    const applyPinch = (canvas) => {
      if (!pinchState || pinchState.canvasId !== canvas.id) return;
      const points = pinchState.pointerIds.map((id) => touchPoints.get(id)).filter(Boolean);
      if (points.length < 2) return;
      const [a, b] = points;
      setZoomRange(pinchZoomWindow({
        anchorZoom: pinchState.anchorZoom,
        scale: touchDistance(a, b) / pinchState.startDistance,
        anchorMidRatio: pinchState.anchorMidRatio,
        currentMidRatio: ratioFromClientX(canvas, (a.clientX + b.clientX) / 2),
      }));
      scheduleDrawAll();
    };

    const beginTouchPointer = (canvas, event) => {
      touchPoints.set(event.pointerId, {
        pointerId: event.pointerId,
        canvasId: canvas.id,
        clientX: event.clientX,
        clientY: event.clientY,
      });
      canvas.setPointerCapture?.(event.pointerId);
      const canvasTouches = touchPointsForCanvas(canvas.id);
      if (canvasTouches.length === 2) {
        beginPinch(canvas, canvasTouches);
        return;
      }
      if (canvasTouches.length > 2) {
        ignoredTouchPointerIds.add(event.pointerId);
        return;
      }
      touchTapCandidate = {
        pointerId: event.pointerId,
        canvasId: canvas.id,
        clientX: event.clientX,
        clientY: event.clientY,
        time: event.timeStamp,
      };
      const mode = dragModeForCanvas(canvas);
      const regionMode = ["gami-sweep", "pilot-observation"].includes(mode)
        ? mode
        : selectRegionActive ? "zoom" : null;
      updateCursorPosition(canvas, event);
      const ratio = ratioFromClientX(canvas, event.clientX);
      if (regionMode) {
        setDragSelection({ canvasId: canvas.id, start: ratio, current: ratio, mode: regionMode });
        setScrubRatio(absoluteRatio(ratio));
        if (["gami-sweep", "pilot-observation"].includes(regionMode)) {
          showMarkedWindowCursorTime(canvas, event, ratio);
        }
        scheduleMarkerUpdate(canvas.id);
        return;
      }
      // Default one-finger touch inspects: move the crosshair, never pan/zoom.
      updateFromClientX(canvas, event.clientX);
    };

    // Returns true when the move was consumed by the touch layer (pinch or an
    // ignored leftover finger); false falls through to the shared drag/inspect
    // pointermove path below.
    const handleTouchPointerMove = (canvas, event) => {
      const point = touchPoints.get(event.pointerId);
      if (point) {
        point.clientX = event.clientX;
        point.clientY = event.clientY;
      }
      if (touchTapCandidate?.pointerId === event.pointerId) {
        const drift = Math.hypot(
          event.clientX - touchTapCandidate.clientX,
          event.clientY - touchTapCandidate.clientY,
        );
        if (drift > TOUCH_TAP_SLOP_PX) touchTapCandidate = null;
      }
      if (pinchState?.canvasId === canvas.id && pinchState.pointerIds.includes(event.pointerId)) {
        applyPinch(canvas);
        return true;
      }
      return ignoredTouchPointerIds.has(event.pointerId);
    };

    const endTouchPointer = (canvas, event, { canceled = false } = {}) => {
      const wasTracked = touchPoints.delete(event.pointerId);
      ignoredTouchPointerIds.delete(event.pointerId);
      if (pinchState?.pointerIds.includes(event.pointerId)) {
        // The finger that stays down after a pinch must not start scrubbing.
        pinchState.pointerIds.forEach((id) => {
          if (id !== event.pointerId && touchPoints.has(id)) ignoredTouchPointerIds.add(id);
        });
        pinchState = null;
        return;
      }
      if (!wasTracked || canceled || touchTapCandidate?.pointerId !== event.pointerId) {
        if (touchTapCandidate?.pointerId === event.pointerId) touchTapCandidate = null;
        return;
      }
      const candidate = touchTapCandidate;
      touchTapCandidate = null;
      if (event.timeStamp - candidate.time > TOUCH_TAP_MAX_MS) {
        lastTouchTap = null;
        return;
      }
      const tap = { canvasId: canvas.id, clientX: event.clientX, clientY: event.clientY, time: event.timeStamp };
      const isDoubleTap = lastTouchTap
        && lastTouchTap.canvasId === tap.canvasId
        && tap.time - lastTouchTap.time <= TOUCH_DOUBLE_TAP_MAX_GAP_MS
        && Math.hypot(tap.clientX - lastTouchTap.clientX, tap.clientY - lastTouchTap.clientY) <= TOUCH_DOUBLE_TAP_SLOP_PX;
      if (isDoubleTap) {
        lastTouchTap = null;
        zoomAroundClientX(canvas, tap.clientX, TOUCH_DOUBLE_TAP_ZOOM_FACTOR);
        return;
      }
      lastTouchTap = tap;
    };

    canvases.forEach((canvas) => {
      const finishCanvasDrag = (event) => {
        const dragSelection = getDragSelection();
        if (dragSelection?.canvasId !== canvas.id) return;
        try {
          if (event?.pointerId !== undefined && canvas.hasPointerCapture?.(event.pointerId)) {
            canvas.releasePointerCapture(event.pointerId);
          }
        } catch {
          // Pointer capture may already be gone if the pointer left the document.
        }
        if (dragSelection.mode === "pan") {
          setDragSelection(null);
          updateCanvasCursors();
          scheduleDrawAll();
          return;
        }
        if (dragSelection.mode === "gami-sweep") {
          hideExplorerGamiCursorTime();
          finishExplorerGamiSweepDrag();
          return;
        }
        if (dragSelection.mode === "pilot-observation") {
          hideExplorerGamiCursorTime();
          finishExplorerPilotObservationDrag();
          return;
        }
        if (canvas.id === "explorerChart") hideExplorerGamiCursorTime();
        finishZoomDrag();
      };

      canvas.addEventListener("pointermove", (event) => {
        if (event.pointerType === "touch" && handleTouchPointerMove(canvas, event)) return;
        const dragSelection = getDragSelection();
        if (dragSelection?.canvasId === canvas.id && dragSelection.mode === "pan") {
          if (event.pointerType === "mouse" && event.buttons === 0) {
            finishCanvasDrag(event);
            return;
          }
          // Pan relative to the zoom window captured at pointerdown so the
          // grabbed point tracks the cursor (drag right -> view moves earlier).
          const currentRatio = ratioFromClientX(canvas, event.clientX);
          const width = dragSelection.anchorZoom.end - dragSelection.anchorZoom.start;
          const deltaVisible = currentRatio - dragSelection.start;
          let start = dragSelection.anchorZoom.start - deltaVisible * width;
          start = Math.max(0, Math.min(start, 1 - width));
          setZoomRange({ start, end: start + width });
          scheduleDrawAll();
          return;
        }
        if (dragSelection?.canvasId === canvas.id) {
          if (event.pointerType === "mouse" && event.buttons === 0) {
            finishCanvasDrag(event);
            return;
          }
          if (!pointerInsideChartPlot(canvas, event)) return;
          updateCursorPosition(canvas, event);
          dragSelection.current = ratioFromClientX(canvas, event.clientX);
          setDragSelection(dragSelection);
          setScrubRatio(absoluteRatio(dragSelection.current));
          if (["gami-sweep", "pilot-observation"].includes(dragSelection.mode)) {
            showMarkedWindowCursorTime(canvas, event, dragSelection.current);
          }
          scheduleMarkerUpdate(canvas.id);
          return;
        }
        if (!pointerInsideChartPlot(canvas, event)) return;
        updateCursorPosition(canvas, event);
        updateFromClientX(canvas, event.clientX);
      });
      // Right-drag pans the zoomed chart (same pan mode as spacebar-held
      // drag); the browser context menu is suppressed on chart canvases so
      // the gesture is usable.
      canvas.addEventListener("contextmenu", (event) => {
        event.preventDefault();
      });
      canvas.addEventListener("pointerdown", (event) => {
        if (!pointerInsideChartPlot(canvas, event)) return;
        if (event.pointerType === "touch") {
          beginTouchPointer(canvas, event);
          return;
        }
        if (event.button === 2) {
          if (!isChartZoomed()) return;
          canvas.setPointerCapture?.(event.pointerId);
          const ratio = ratioFromClientX(canvas, event.clientX);
          const zoomRange = getZoomRange();
          setDragSelection({
            canvasId: canvas.id,
            mode: "pan",
            start: ratio,
            current: ratio,
            anchorZoom: { start: zoomRange.start, end: zoomRange.end },
          });
          updateCanvasCursors();
          return;
        }
        if (event.button !== 0) return;
        const mode = dragModeForCanvas(canvas);
        if (mode === "pan") {
          canvas.setPointerCapture?.(event.pointerId);
          const ratio = ratioFromClientX(canvas, event.clientX);
          const zoomRange = getZoomRange();
          setDragSelection({
            canvasId: canvas.id,
            mode: "pan",
            start: ratio,
            current: ratio,
            anchorZoom: { start: zoomRange.start, end: zoomRange.end },
          });
          updateCanvasCursors();
          return;
        }
        updateCursorPosition(canvas, event);
        canvas.setPointerCapture?.(event.pointerId);
        const ratio = ratioFromClientX(canvas, event.clientX);
        setDragSelection({ canvasId: canvas.id, start: ratio, current: ratio, mode });
        setScrubRatio(absoluteRatio(ratio));
        if (["gami-sweep", "pilot-observation"].includes(mode)) {
          showMarkedWindowCursorTime(canvas, event, ratio);
        }
        scheduleMarkerUpdate(canvas.id);
      });
      canvas.addEventListener("pointerup", (event) => {
        if (event.pointerType === "touch") endTouchPointer(canvas, event);
        finishCanvasDrag(event);
      });
      canvas.addEventListener("pointercancel", (event) => {
        if (event.pointerType === "touch") endTouchPointer(canvas, event, { canceled: true });
        setDragSelection(null);
        if (canvas.id === "explorerChart") hideExplorerGamiCursorTime();
        scheduleMarkerUpdate(canvas.id);
      });
      canvas.addEventListener("lostpointercapture", (event) => {
        if (event.pointerType === "touch") endTouchPointer(canvas, event, { canceled: true });
        finishCanvasDrag(event);
      });
      canvas.addEventListener("pointerenter", () => {
        hoveredCanvas = canvas;
        updateCanvasCursors();
      });
      canvas.addEventListener("pointerleave", () => {
        // Keep the hover target while a drag is mid-flight (pointer capture
        // can carry the pointer briefly outside the canvas bounds).
        if (getDragSelection()?.canvasId === canvas.id) return;
        if (hoveredCanvas === canvas) hoveredCanvas = null;
      });
    });

    // Spacebar engages pan mode; arrow keys nudge the window. Both act only
    // while the pointer is over a chart so normal page scrolling is preserved.
    const isTypingTarget = (target) => {
      const tag = target?.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable === true;
    };

    window.addEventListener("keydown", (event) => {
      if (event.code === "Space") {
        if (!hoveredCanvas || isTypingTarget(event.target)) return;
        event.preventDefault();
        if (!spaceHeld) {
          spaceHeld = true;
          updateCanvasCursors();
        }
        return;
      }
      if (event.code === "ArrowLeft" || event.code === "ArrowRight") {
        if (!hoveredCanvas || isTypingTarget(event.target) || !isChartZoomed()) return;
        event.preventDefault();
        const zoomRange = getZoomRange();
        const step = (zoomRange.end - zoomRange.start) * 0.1;
        panZoomBy(event.code === "ArrowRight" ? step : -step);
        scheduleDrawAll();
      }
    });

    window.addEventListener("keyup", (event) => {
      if (event.code !== "Space" || !spaceHeld) return;
      spaceHeld = false;
      cancelActivePanDrag();
      updateCanvasCursors();
    });

    window.addEventListener("blur", () => {
      if (!spaceHeld) return;
      spaceHeld = false;
      cancelActivePanDrag();
      updateCanvasCursors();
    });

    document.querySelectorAll(".reset-zoom").forEach((button) => {
      button.addEventListener("click", () => {
        setZoomRange({ start: 0, end: 1 });
        setScrubRatio(0.62);
        setDragSelection(null);
        scheduleDrawAll();
      });
    });

    // Zoom step buttons work for mouse and touch alike. They center on the
    // scrub cursor when it sits inside the visible window (inspect a moment,
    // then zoom into it), otherwise on the window center.
    document.querySelectorAll("[data-chart-zoom-step]").forEach((button) => {
      button.addEventListener("click", () => {
        const zoomRange = getZoomRange();
        const factor = button.dataset.chartZoomStep === "in" ? 0.5 : 2;
        const scrub = getScrubRatio();
        const center = scrub >= zoomRange.start && scrub <= zoomRange.end
          ? scrub
          : (zoomRange.start + zoomRange.end) / 2;
        setZoomRange(steppedZoomWindow(zoomRange, factor, center));
        scheduleDrawAll();
      });
    });

    document.querySelectorAll("[data-chart-select-region]").forEach((button) => {
      button.addEventListener("click", () => {
        setChartSelectRegionActive(!selectRegionActive);
      });
    });

    refreshChartGestureHints();
  }

  function finishZoomDrag() {
    const dragSelection = getDragSelection();
    if (!dragSelection) return;
    if (dragSelection.mode && dragSelection.mode !== "zoom") return;
    const start = Math.min(dragSelection.start, dragSelection.current);
    const end = Math.max(dragSelection.start, dragSelection.current);
    if (end - start > 0.035) {
      const zoomRange = getZoomRange();
      const visibleWidth = zoomRange.end - zoomRange.start;
      const nextRange = {
        start: zoomRange.start + start * visibleWidth,
        end: zoomRange.start + end * visibleWidth,
      };
      setZoomRange(nextRange);
      setScrubRatio((nextRange.start + nextRange.end) / 2);
      // Select Region is a one-shot mode: leave it once a selection lands
      // (a too-small drag keeps it active so the user can retry).
      if (selectRegionActive) setChartSelectRegionActive(false);
    }
    setDragSelection(null);
    scheduleDrawAll();
  }

  function finishExplorerGamiSweepDrag() {
    const dragSelection = getDragSelection();
    if (!dragSelection || dragSelection.canvasId !== "explorerChart") return;
    const start = Math.min(dragSelection.start, dragSelection.current);
    const end = Math.max(dragSelection.start, dragSelection.current);
    if (end - start > 0.005) {
      const data = explorerPhaseFilteredData();
      const startPoint = nearestPoint(data, start);
      const endPoint = nearestPoint(data, end);
      commitExplorerGamiSweepSelection({ startPoint, endPoint, startRatio: start, endRatio: end });
    }
    setDragSelection(null);
    hideExplorerGamiCursorTime();
    scheduleDrawAll();
  }

  function finishExplorerPilotObservationDrag() {
    const dragSelection = getDragSelection();
    if (!dragSelection || dragSelection.canvasId !== "explorerChart") return;
    const start = Math.min(dragSelection.start, dragSelection.current);
    const end = Math.max(dragSelection.start, dragSelection.current);
    if (end - start > 0.005) {
      const data = explorerPhaseFilteredData();
      const startPoint = nearestPoint(data, start);
      const endPoint = nearestPoint(data, end);
      commitExplorerPilotObservationSelection({ startPoint, endPoint, startRatio: start, endRatio: end });
    }
    setDragSelection(null);
    hideExplorerGamiCursorTime();
    scheduleDrawAll();
  }

  function setExplorerCursorRatio(ratio, options = {}) {
    const safeRatio = clamp01(ratio);
    const zoomRange = getZoomRange();
    setScrubRatio(zoomRange.start + safeRatio * (zoomRange.end - zoomRange.start));
    if (options.draw === false) {
      if (options.renderContext === false) {
        if (options.schedule === false) {
          updateExplorerChartInteraction({ renderContext: false });
        } else {
          scheduleExplorerInteractionUpdate();
        }
        return;
      }
      updateExplorerChartInteraction({ renderContext: true });
      return;
    }
    const data = explorerPhaseFilteredData();
    const point = nearestPoint(data, safeRatio);
    if (point) updateScrubReadout(point);
    drawExplorerChart();
  }

  function scheduleExplorerInteractionUpdate() {
    if (getExplorerInteractionUpdateScheduled()) return;
    setExplorerInteractionUpdateScheduled(true);
    requestAnimationFrame(() => {
      setExplorerInteractionUpdateScheduled(false);
      updateExplorerChartInteraction({ renderContext: false });
    });
  }

  function setExplorerCursorElapsed(elapsedSeconds) {
    const data = explorerPhaseFilteredData();
    const ratio = explorerTimelineRatioForElapsed(data, elapsedSeconds);
    if (ratio === null) return;
    setExplorerCursorRatio(ratio, { draw: false });
  }

  function explorerTimelineControlIcon(name) {
    return `<span class="playback-control-icon playback-control-icon-${name}" aria-hidden="true"></span>`;
  }

  function explorerFindingMarkers(markers = buildExplorerTimelineMarkers(explorerPhaseFilteredData())) {
    return markers.filter((marker) => marker.type === "finding" && Number.isFinite(marker.time));
  }

  function explorerFindingMarkerPosition(markers, currentElapsed) {
    if (!markers.length || !Number.isFinite(currentElapsed)) return -1;
    const epsilon = 0.05;
    return markers.findIndex((marker) => Math.abs(marker.time - currentElapsed) <= epsilon);
  }

  function jumpExplorerFindingMarker(direction) {
    const data = explorerPhaseFilteredData();
    const markers = explorerFindingMarkers(buildExplorerTimelineMarkers(data));
    if (!markers.length) return;
    const current = nearestPoint(data, visibleScrubRatio())?.elapsedSeconds ?? data[0]?.elapsedSeconds ?? 0;
    const epsilon = 0.05;
    const target = direction > 0
      ? markers.find((marker) => marker.time > current + epsilon) ?? markers[markers.length - 1]
      : [...markers].reverse().find((marker) => marker.time < current - epsilon) ?? markers[0];
    if (Number.isFinite(target.ratio)) {
      setExplorerCursorRatio(target.ratio, { draw: false });
      return;
    }
    setExplorerCursorElapsed(target.time);
  }

  function setupExplorerContextInteractions() {
    const panel = document.querySelector("#graphExplorerPanel");
    if (!panel) return;
    panel.addEventListener("click", (event) => {
      const closeAlert = event.target.closest("[data-explorer-alert-close]");
      if (closeAlert) {
        setExplorerSelectedAlertId(null);
        renderExplorerContext();
        return;
      }

      const alertJump = event.target.closest("[data-explorer-alert-jump]");
      if (alertJump) {
        setExplorerCursorElapsed(Number(alertJump.dataset.explorerAlertJump));
        return;
      }

      const timelineStep = event.target.closest("[data-explorer-timeline-step]");
      if (timelineStep) {
        jumpExplorerFindingMarker(Number(timelineStep.dataset.explorerTimelineStep));
        return;
      }

      const alertButton = event.target.closest("[data-explorer-alert-id]");
      if (alertButton) {
        const id = alertButton.dataset.explorerAlertId;
        setExplorerSelectedAlertId(getExplorerSelectedAlertId() === id ? null : id);
        renderExplorerContext();
        return;
      }

      const marker = event.target.closest("[data-explorer-marker-ratio]");
      if (marker) {
        setExplorerCursorRatio(Number(marker.dataset.explorerMarkerRatio), { draw: false });
      }
    });

    panel.addEventListener("pointerdown", (event) => {
      if (event.target.closest("[data-explorer-timeline-step]")) return;
      const timeline = event.target.closest(".explorer-timeline .playback-timeline");
      const timelineBody = timeline?.querySelector(".playback-timeline-body");
      if (!timelineBody) return;
      event.preventDefault();
      const update = (pointerEvent) => {
        const rect = timelineBody.getBoundingClientRect();
        if (!rect.width) return;
        const ratio = (pointerEvent.clientX - rect.left) / rect.width;
        setExplorerCursorRatio(ratio, { draw: false, renderContext: false });
        if (getExplorerPilotObservationPanelOpen() || getExplorerGamiSweepPanelOpen()) {
          showExplorerGamiCursorTime({ clientX: pointerEvent.clientX, clientY: pointerEvent.clientY, ratio });
        }
      };
      update(event);
      try {
        timelineBody.setPointerCapture?.(event.pointerId);
      } catch {
        // Window listeners below keep scrubbing active when delegated capture is unavailable.
      }
      const move = (pointerEvent) => update(pointerEvent);
      const up = () => {
        try {
          timelineBody.releasePointerCapture?.(event.pointerId);
        } catch {
          // Capture may already be released if the pointer leaves the document.
        }
        renderExplorerContext();
        hideExplorerGamiCursorTime();
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    });
  }

  return {
    setupChartScrubber,
    finishZoomDrag,
    finishExplorerGamiSweepDrag,
    finishExplorerPilotObservationDrag,
    setExplorerCursorRatio,
    scheduleExplorerInteractionUpdate,
    setExplorerCursorElapsed,
    explorerTimelineControlIcon,
    explorerFindingMarkers,
    explorerFindingMarkerPosition,
    jumpExplorerFindingMarker,
    setupExplorerContextInteractions,
    chartGestureHintBaseText,
    refreshChartGestureHints,
    getChartSelectRegionActive: () => selectRegionActive,
    setChartSelectRegionActive,
  };
}
